import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import YoutubeEmbed from "./YoutubeEmbed";
import {useSelector} from "react-redux"; // <- use your component

export default function VideoInfoGrid({
                                          apiPath,            // e.g. "aftesi_te_kufizuar" (provided by parent)
                                          truncateAt = 100,
                                          readMoreLabel = "Read more",
                                          closeLabel = "Close",
                                          showVideo = true,   // toggle if you want
                                      }) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [openIndex, setOpenIndex] = useState(null);
    const lang = useSelector(state => state.language.currentLanguage)

    useEffect(() => {
        const ac = new AbortController();
        setLoading(true);
        setErr(null);

        fetch(`/api/data_video/video/${apiPath}?lang=${encodeURIComponent(lang)}`, { signal: ac.signal })
            .then(async (r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => setRecords(Array.isArray(data?.records) ? data.records : []))
            .catch((e) => { if (e.name !== "AbortError") setErr(e.message || "Failed to load"); })
            .finally(() => setLoading(false));

        return () => ac.abort();
    }, [apiPath, lang]);

    const truncate = (str, max = truncateAt) =>
        typeof str === "string" && str.length > max ? str.slice(0, max) + "..." : str ?? "";

    if (loading) return <div className="p-6 text-center">Loading…</div>;
    if (err) return <div className="p-6 text-center text-red-600">Error: {err}</div>;

    return (
        <>
            <div className="container mx-auto px-4 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                    {records.map((rec, i) => {
                        const needsMore = (rec?.description?.length || 0) > truncateAt;

                        return (
                            <div key={rec.id ?? i} className="flex flex-col">
                                <p className="text-justify mb-4 font-semibold text-red-600">
                                    {rec?.name || ""}
                                </p>
                                {rec?.description && (
                                    <p className="text-justify text-[14px] text-gray-700">
                                        {truncate(rec.description, truncateAt)}
                                        {needsMore && (
                                            <>
                                                {" "}
                                                <button
                                                    onClick={() => setOpenIndex(i)}
                                                    className="text-blue-600 underline cursor-pointer select-none"
                                                >
                                                    {readMoreLabel}
                                                </button>
                                            </>
                                        )}
                                    </p>
                                )}
                                <div className="w-full aspect-video mb-4">
                                    <YoutubeEmbed embedId={rec.url} height={300} />
                                </div>


                            </div>
                        );
                    })}
                </div>
            </div>

            {/* MUI Modal for full description (no video inside) */}
            <Modal
                open={openIndex !== null}
                onClose={() => setOpenIndex(null)}
                aria-labelledby={`video-desc-modal-title-${openIndex ?? 0}`}
                aria-describedby={`video-desc-modal-desc-${openIndex ?? 0}`}
            >
                <Box
                    id={`video-desc-modal-${openIndex ?? 0}`}
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 600 },
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                    }}
                >
                    <div className="flex items-center justify-between">
                        <Typography id={`video-desc-modal-title-${openIndex ?? 0}`} variant="h6" component="h3">
                            {records[openIndex ?? 0]?.name || ""}
                        </Typography>
                        <IconButton aria-label={closeLabel} onClick={() => setOpenIndex(null)}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <Typography
                        id={`video-desc-modal-desc-${openIndex ?? 0}`}
                        sx={{ mt: 2, whiteSpace: "pre-wrap" }}
                    >
                        {records[openIndex ?? 0]?.description || ""}
                    </Typography>

                    <div className="flex justify-end mt-4">
                        <Button variant="contained" onClick={() => setOpenIndex(null)}>
                            {closeLabel}
                        </Button>
                    </div>
                </Box>
            </Modal>
        </>
    );
}
